import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../store/AuthContext';
import { feedbackApi } from '../../api/feedback.api';
import { useUI } from '../../store/UIContext';
import { PageHeader, Spinner, EmptyState, Btn, Modal } from '../../components/ui/index';
import { toPersian } from '../../utils/persianNumber';
import { toGregorianDateParams, startOfDay, addDays } from '../../utils/jalali';
import JalaliDateRangePicker from '../../components/calendar/JalaliDateRangePicker';
import { useAutoRefresh } from '../../hooks/useAutoRefresh';
import { Star, ChartLine } from '@phosphor-icons/react';

function Stars({ value }) {
  const rounded = Math.round(value || 0);
  return (
    <span style={{ letterSpacing: 1 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={14} weight={n <= rounded ? 'fill' : 'regular'} color={n <= rounded ? '#f59e0b' : '#cbd5e1'} />
      ))}
    </span>
  );
}

const toDateParam = (date) => {
  if (!date) return undefined;
  const { year, month, day } = toGregorianDateParams(date);
  return `${year}-${month}-${day}`;
};

// Minimal self-contained line chart (no chart library dependency, matching
// this project's convention - see JalaliDateRangePicker). `points` is
// [{date, value}], value may be null for days with no data (rendered as a
// gap, not a zero).
function TrendChart({ points, color, unitLabel, formatValue }) {
  if (!points || points.length === 0) {
    return <div style={{ fontSize: 12, color: '#94a3b8', padding: '20px 0', textAlign: 'center' }}>داده‌ای برای این بازه نیست</div>;
  }
  const w = 560, h = 140, padX = 8, padY = 16;
  const values = points.map((p) => p.value).filter((v) => v != null);
  const max = values.length ? Math.max(...values) : 1;
  const min = values.length ? Math.min(...values) : 0;
  const span = max - min || 1;
  const stepX = points.length > 1 ? (w - padX * 2) / (points.length - 1) : 0;
  const toY = (v) => h - padY - ((v - min) / span) * (h - padY * 2);

  let path = '';
  let started = false;
  points.forEach((p, i) => {
    const x = padX + i * stepX;
    if (p.value == null) { started = false; return; }
    const y = toY(p.value);
    path += started ? ` L ${x} ${y}` : ` M ${x} ${y}`;
    started = true;
  });

  const lastValid = [...points].reverse().find((p) => p.value != null);

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ display: 'block' }}>
        <path d={path} fill="none" stroke={color} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
        {points.map((p, i) => {
          if (p.value == null) return null;
          const x = padX + i * stepX;
          const y = toY(p.value);
          return <circle key={i} cx={x} cy={y} r={2.5} fill={color} />;
        })}
      </svg>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: '#94a3b8', marginTop: 4 }}>
        <span>{points[0].date}</span>
        {lastValid && <span style={{ color, fontWeight: 700 }}>{formatValue(lastValid.value)} {unitLabel}</span>}
        <span>{points[points.length - 1].date}</span>
      </div>
    </div>
  );
}

// Manager_employee (own department) / dispatcher (all departments) only -
// this is exactly the data the employee themselves is never allowed to see
// (see ResponseRating.jsx's comment and the backend's getMyResponseRating
// vs getDepartmentResponseRatings split).
export default function EmployeeResponseRatingsPage() {
  const { addToast } = useUI();
  const navigate = useNavigate();
  const { user } = useAuth();
  const seesAllDepartments = user?.type === 'admin' || user?.type === 'dispatcher';
  const [rows, setRows] = useState([]);
  const [ratingSeries, setRatingSeries] = useState([]);
  const [timeSeries, setTimeSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [details, setDetails] = useState([]);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [ctx, setCtx] = useState(null);
  const [ctxLoading, setCtxLoading] = useState(false);
  const openContext = async (ratingId) => {
    setCtx({}); setCtxLoading(true);
    try {
      const res = await feedbackApi.getRatingContext(ratingId);
      setCtx(res.data);
    } catch {
      setCtx(null);
      addToast('خطا در دریافت جزئیات', 'error');
    }
    setCtxLoading(false);
  };

  const today = startOfDay(new Date());
  const [startDate, setStartDate] = useState(addDays(today, -29));
  const [endDate, setEndDate] = useState(today);

  const dateParams = { date_from: toDateParam(startDate), date_to: toDateParam(endDate) };

  const load = useCallback((showSpinner = true) => {
    if (showSpinner) setLoading(true);
    return Promise.all([
      feedbackApi.getDepartmentResponseRatings(dateParams),
      feedbackApi.getResponseRatingTimeSeries(dateParams),
      feedbackApi.getResponseTimeSeries(dateParams),
    ])
      .then(([summary, ratingTs, timeTs]) => {
        setRows(Array.isArray(summary.data) ? summary.data : []);
        setRatingSeries(Array.isArray(ratingTs.data) ? ratingTs.data : []);
        setTimeSeries(Array.isArray(timeTs.data) ? timeTs.data : []);
      })
      .catch(() => { if (showSpinner) addToast('خطا در دریافت امتیازها', 'error'); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addToast, dateParams.date_from, dateParams.date_to]);

  useEffect(() => { load(); }, [load]);
  useAutoRefresh(() => load(false), { paused: !!expanded });

  const toggleExpand = async (employeeId) => {
    if (expanded === employeeId) {
      setExpanded(null);
      return;
    }
    setExpanded(employeeId);
    setDetailsLoading(true);
    try {
      const res = await feedbackApi.getEmployeeResponseRatingDetails(employeeId, dateParams);
      setDetails(Array.isArray(res.data) ? res.data : []);
    } catch {
      addToast('خطا در دریافت جزئیات', 'error');
    } finally {
      setDetailsLoading(false);
    }
  };

  const overallAvgRating = rows.length
    ? rows.reduce((s, r) => s + (r.avg_rating || 0) * r.rating_count, 0) / Math.max(1, rows.reduce((s, r) => s + r.rating_count, 0))
    : null;
  const deptRows = Object.values(rows.reduce((acc, r) => {
    const key = r.department_name || 'بدون دپارتمان';
    const d = acc[key] || (acc[key] = { name: key, sum: 0, count: 0, employees: 0 });
    d.sum += (r.avg_rating || 0) * r.rating_count;
    d.count += r.rating_count;
    d.employees += 1;
    return acc;
  }, {})).map((d) => ({ ...d, avg: d.count ? d.sum / d.count : 0 })).sort((a, b) => b.avg - a.avg);
  const overallAvgMinutes = timeSeries.length
    ? timeSeries.reduce((s, p) => s + (p.avg_minutes || 0) * p.count, 0) / Math.max(1, timeSeries.reduce((s, p) => s + p.count, 0))
    : null;

  return (
    <div className="fade-in">
      <PageHeader
        title="نظرسنجی"
        subtitle="امتیاز ۱ تا ۵ که مشتریان به پاسخ هر کارمند داده‌اند - این اطلاعات فقط برای مدیر دپارتمان، دیسپچر و ادمین قابل مشاهده است، خود کارمند آن را نمی‌بیند"
      />

      <div style={{ marginBottom: 20 }}>
        <JalaliDateRangePicker startDate={startDate} endDate={endDate} onChange={(s, e) => { if (s) setStartDate(s); if (e) setEndDate(e); }} />
      </div>

      {loading ? <Spinner /> : (
        <>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 20px', minWidth: 160 }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>میانگین امتیاز کل</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{overallAvgRating != null ? toPersian(overallAvgRating.toFixed(2)) : '—'}</div>
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 20px', minWidth: 160 }}>
              <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 6 }}>میانگین زمان پاسخ‌گویی</div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#0f172a' }}>{overallAvgMinutes != null ? `${toPersian(Math.round(overallAvgMinutes))} دقیقه` : '—'}</div>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 14, marginBottom: 20, maxWidth: 1180 }}>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 10 }}>
                <Star size={14} weight="fill" color="#f59e0b" /> روند میانگین امتیاز
              </div>
              <TrendChart
                points={ratingSeries.map((p) => ({ date: p.date, value: p.avg_rating }))}
                color="#f59e0b"
                unitLabel=""
                formatValue={(v) => toPersian(v.toFixed(1))}
              />
            </div>
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, fontWeight: 700, color: '#334155', marginBottom: 10 }}>
                <ChartLine size={14} weight="regular" color="#29AAE1" /> روند زمان پاسخ‌گویی (دقیقه)
              </div>
              <TrendChart
                points={timeSeries.map((p) => ({ date: p.date, value: p.avg_minutes }))}
                color="#29AAE1"
                unitLabel="دقیقه"
                formatValue={(v) => toPersian(Math.round(v))}
              />
            </div>
          </div>

          {seesAllDepartments && deptRows.length > 0 && (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', maxWidth: 720, marginBottom: 20 }}>
              <div style={{ padding: '12px 18px', fontSize: 13, fontWeight: 700, color: '#334155', borderBottom: '1px solid #f1f5f9' }}>امتیاز به تفکیک دپارتمان</div>
              {deptRows.map((d) => (
                <div key={d.name} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 18px', borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{d.name}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8' }}>{toPersian(d.count)} امتیاز ثبت‌شده - {toPersian(d.employees)} کارمند</div>
                  </div>
                  <Stars value={d.avg} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{toPersian(d.avg.toFixed(1))}</span>
                </div>
              ))}
            </div>
          )}

          {rows.length === 0 ? (
            <EmptyState icon={<Star size={44} weight="regular" />} title="در این بازه زمانی امتیازی ثبت نشده است" />
          ) : (
            <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', maxWidth: 720 }}>
              {rows.map((r) => (
                <div key={r.employee_id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>{r.name}</div>
                      <div style={{ fontSize: 11, color: '#94a3b8' }}>
                        {toPersian(r.rating_count)} امتیاز ثبت‌شده
                        {seesAllDepartments && r.department_name ? ` - ${r.department_name}` : ''}
                      </div>
                      {r.companies && <div style={{ fontSize: 11, color: '#2563eb' }}>🏢 از طرف: {r.companies}</div>}
                    </div>
                    <Stars value={r.avg_rating} />
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#334155' }}>{toPersian(r.avg_rating?.toFixed(1) ?? '0')}</span>
                    <Btn variant="ghost" size="sm" onClick={() => toggleExpand(r.employee_id)}>
                      {expanded === r.employee_id ? 'بستن' : 'جزئیات'}
                    </Btn>
                  </div>
                  {expanded === r.employee_id && (
                    <div style={{ padding: '0 18px 14px', background: '#f8faff' }}>
                      {detailsLoading ? <Spinner /> : details.length === 0 ? (
                        <div style={{ fontSize: 12, color: '#94a3b8', padding: '10px 0' }}>موردی یافت نشد</div>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingTop: 8 }}>
                          {details.map((d, i) => (
                            <div key={i} style={{ background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span
                                  role="link" tabIndex={0}
                                  onClick={() => openContext(d.id)}
                                  onKeyDown={(e) => { if (e.key === 'Enter') openContext(d.id); }}
                                  title="مشاهده پاسخ و امتیاز"
                                  style={{ fontSize: 12, color: '#2563eb', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                                >{d.ticket_title || `تیکت #${toPersian(d.ticket_id)}`}</span>
                                <Stars value={d.rating} />
                              </div>
                              <div style={{ fontSize: 11, color: '#64748b' }}>{d.company ? `🏢 از طرف: ${d.company}` : '👤 کاربر مهمان'}</div>
                              {d.comment && <div style={{ fontSize: 12, color: '#64748b' }}>{d.comment}</div>}
                              <div style={{ fontSize: 10, color: '#94a3b8' }}>{d.created_date}</div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <Modal open={!!ctx} onClose={() => setCtx(null)} title="جزئیات امتیاز" width={560}>
        {ctxLoading || !ctx ? <Spinner /> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12, fontSize: 13, color: '#334155', lineHeight: 1.9 }}>
            <div><b>{ctx.ticket_title || `تیکت #${toPersian(ctx.ticket_id)}`}</b> - {ctx.company ? `🏢 ${ctx.company}` : 'کاربر مهمان'}</div>
            <div><div style={{ fontSize: 11, color: '#94a3b8' }}>پیام مشتری</div><div style={{ background: '#f8faff', borderRadius: 8, padding: '8px 12px', whiteSpace: 'pre-wrap' }}>{ctx.customer_message || '—'}</div></div>
            <div><div style={{ fontSize: 11, color: '#94a3b8' }}>پاسخ {ctx.employee}</div><div style={{ background: '#f8faff', borderRadius: 8, padding: '8px 12px', whiteSpace: 'pre-wrap' }}>{ctx.reply || '—'}</div></div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}><Stars value={ctx.rating} /> <span>{toPersian(ctx.rating)} از ۵</span></div>
            {ctx.comment && <div><div style={{ fontSize: 11, color: '#94a3b8' }}>توضیح مشتری</div>{ctx.comment}</div>}
          </div>
        )}
      </Modal>
    </div>
  );
}
